import React, { useState, useEffect } from 'react';
import { Bell, Shield, Database, Volume2, Check, AlertCircle } from 'lucide-react';
import { GradientButton } from '../ui/GradientButton';
import { colors } from '../../styles/designSystem';
import ConfigCard from './ConfigCard';
import ConfigToggle from './ConfigToggle';
import log from '../../logger';
import api from '../../api';

const ConfigurationSection = ({ onLoading, onMessage, onError }) => {
  // Define available voices
  const availableVoices = [
    { id: 'alloy', name: 'Alloy' },
    { id: 'echo', name: 'Echo' },
    { id: 'shimmer', name: 'Shimmer' },
    // { id: 'ash', name: 'ash' },
    // { id: 'ballad', name: 'ballad' },
    // { id: 'coral', name: 'coral' },
    // { id: 'sage', name: 'sage' },
    // { id: 'verse', name: 'verse' },
  ];
  const defaultConfig = {
    notifications: {
      email: true,
      push: false,
      digest: true,
    },
    security: {
      twoFactor: true,
      ipRestriction: false,
      sessionTimeout: '30',
    },
    system: {
      autoResponse: true,
      multilingual: false,
      debug: false,
    },
    voice: {
      enabled: true,
      volume: '80',
      speed: '1',
      selectedVoice: 'voice1',
    },
  };

  // Initialize config state
  const [config, setConfig] = useState(defaultConfig);

  const [loading, setLoadingState] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch existing system configuration from backend when component mounts
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoadingState(true);
        const response = await api.get('/system-configuration'); // Updated endpoint
        setConfig(response.data || defaultConfig);
        setLoadingState(false);
        onLoading && onLoading(false); // Add this line
      } catch (err) {
        log.error('Error fetching system configuration:', err);
        setError('Failed to load system configuration.');
        setLoadingState(false);
      }
    };

    fetchConfig();
  }, []);

  // Handle configuration changes
  const handleConfigChange = (section, key, value) => {
    setConfig((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoadingState(true);
      onLoading && onLoading(true);
  
      // Send updated config to backend
      await api.post('/system-configuration', config);
  
      setMessage('System configuration saved successfully.');
      setError('');
      setLoadingState(false);
      onLoading && onLoading(false); // Add this line
  
      onMessage && onMessage('System configuration saved successfully.');
      onError && onError('');
    } catch (err) {
      console.error('Error saving system configuration:', err);
      setError('Failed to save system configuration.');
      setMessage('');
      setLoadingState(false);
      onLoading && onLoading(false); // Add this line
      onError && onError('Failed to save system configuration.');
      onMessage && onMessage('');
    }
  };
  

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div>
        <h2 className={`${colors.text.heading} text-2xl font-semibold mb-2`}>
          System Configuration
        </h2>
        <p className={`${colors.text.body}`}>
          Customize your system settings and preferences
        </p>
      </div>

      {/* Alert Messages */}
      {(message || error) && (
        <div
          className={`p-4 rounded-xl ${
            message ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
          }`}
        >
          <div className="flex items-center">
            {message ? (
              <Check className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <p>{message || error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Notifications Configuration */}
          <ConfigCard icon={Bell} title="Notifications">
            <div className="space-y-2">
              <ConfigToggle
                label="Email Notifications"
                description="Receive system alerts and updates via email"
                checked={config.notifications.email}
                onChange={(value) => handleConfigChange('notifications', 'email', value)}
              />
              <ConfigToggle
                label="Push Notifications"
                description="Get real-time alerts on your desktop"
                checked={config.notifications.push}
                onChange={(value) => handleConfigChange('notifications', 'push', value)}
              />
              <ConfigToggle
                label="Daily Digest"
                description="Receive a summary of activities every 24 hours"
                checked={config.notifications.digest}
                onChange={(value) => handleConfigChange('notifications', 'digest', value)}
              />
            </div>
          </ConfigCard>

          {/* Security Settings */}
          <ConfigCard icon={Shield} title="Security">
            <div className="space-y-2">
              <ConfigToggle
                label="Two-Factor Authentication"
                description="Add an extra layer of security to your account"
                checked={config.security.twoFactor}
                onChange={(value) => handleConfigChange('security', 'twoFactor', value)}
              />
              <ConfigToggle
                label="IP Restriction"
                description="Limit access to specific IP addresses"
                checked={config.security.ipRestriction}
                onChange={(value) => handleConfigChange('security', 'ipRestriction', value)}
              />
              <div className="py-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Timeout (minutes)
                </label>
                <select
                  value={config.security.sessionTimeout}
                  onChange={(e) =>
                    handleConfigChange('security', 'sessionTimeout', e.target.value)
                  }
                  className="w-full rounded-lg border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
            </div>
          </ConfigCard>

          {/* System Settings */}
          <ConfigCard icon={Database} title="System">
            <div className="space-y-2">
              <ConfigToggle
                label="Auto-Response"
                description="Automatically respond to common queries"
                checked={config.system.autoResponse}
                onChange={(value) => handleConfigChange('system', 'autoResponse', value)}
              />
              <ConfigToggle
                label="Multi-language Support"
                description="Enable support for multiple languages"
                checked={config.system.multilingual}
                onChange={(value) => handleConfigChange('system', 'multilingual', value)}
              />
              <ConfigToggle
                label="Debug Mode"
                description="Enable detailed system logs for troubleshooting"
                checked={config.system.debug}
                onChange={(value) => handleConfigChange('system', 'debug', value)}
              />
            </div>
          </ConfigCard>

          {/* Voice Settings */}
          <ConfigCard icon={Volume2} title="Voice Settings">
            <div className="space-y-4">
              <ConfigToggle
                label="Voice Interaction"
                description="Enable voice commands and responses"
                checked={config.voice.enabled}
                onChange={(value) => handleConfigChange('voice', 'enabled', value)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Volume Level
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={config.voice.volume}
                  onChange={(e) => handleConfigChange('voice', 'volume', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="text-sm text-gray-500 mt-1">{config.voice.volume}%</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Speech Rate
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={config.voice.speed}
                  onChange={(e) => handleConfigChange('voice', 'speed', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="text-sm text-gray-500 mt-1">{config.voice.speed}x</div>
              </div>

              {/* Voice Selection Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Voice
                </label>
                <select
                  value={config.voice.selectedVoice}
                  onChange={(e) =>
                    handleConfigChange('voice', 'selectedVoice', e.target.value)
                  }
                  className="w-full rounded-lg border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                >
                  {availableVoices.map((voice) => (
                    <option key={voice.id} value={voice.id}>
                      {voice.name}
                    </option>
                  ))}
                </select>
                <div className="text-sm text-gray-500 mt-1">
                  Selected Voice:{' '}
                  {availableVoices.find((v) => v.id === config.voice.selectedVoice)?.name}
                </div>
              </div>
            </div>
          </ConfigCard>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              // Reset to default configurations
              setConfig(defaultConfig);
              setMessage('Configuration reset to defaults.');
              setError('');
            }}
            className="px-6 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Reset to Defaults
          </button>
          <GradientButton type="submit" disabled={loading} className="px-6 py-2">
            {loading ? 'Saving...' : 'Save Changes'}
          </GradientButton>
        </div>
      </form>
    </div>
  );
};

export default ConfigurationSection;
